<template>
  <div v-if="hasLink && canAccessLink" class="flex gap-x-16 justify-between">
    <router-link :to="(title as LinkTitle).link" class="link mt-7.5">
    <span class="back-link">
      <Chevron/>
    </span>
      <h1>
      <span v-if="typeof (title as LinkTitle).text === 'string'">
        {{ $t((title as LinkTitle).text as string ?? '') }}
      </span>
        <component :is="(title as LinkTitle).text" v-else/>
      </h1>
    </router-link>
    <div class="grow">
      <Component
        v-if="globalFilterComponent"
        :is="globalFilterComponent?.component"
        v-bind="globalFilterComponent?.props"
        v-on="globalFilterComponent?.events"
      />
    </div>
  </div>

  <h1 v-else-if="title && hasLink" class="mt-7.5">
    {{ $t((title as LinkTitle).text as string ?? '') }}
  </h1>
  <h1 v-else-if="title" class="mt-7.5">
    {{ $t(title as string ?? '') }}
  </h1>
</template>

<script lang="ts" setup>
import {computed, unref} from 'vue';
import Chevron from '@images/chevron.svg?component';
import {useTitle} from '@vueuse/core';
import {useI18n} from 'vue-i18n';
import {type RouteLocationRaw, useLink, useRoute} from 'vue-router';
import {type LinkTitle, type Title, useHeader} from './injects';
import {useStore} from '@/store';
import {canAccess} from '@/utils/user';


const route = useRoute();
const header = useHeader();

const globalFilterComponent = computed(() => header?.globalFilterComponent?.[header?.globalFilterComponent.length - 1])

const store = useStore();

const title = computed<Title>(() => (unref(header?.title?.at(-1)) ?? route.meta.title) as Title);
const isLink = (value: Title): value is LinkTitle => !!value && typeof value === 'object';
const hasLink = computed(() => isLink(title.value));
const link = useLink({to: computed(() => title.value?.link as RouteLocationRaw ?? {name: 'home'})});
const canAccessLink = computed(() => {
  if (!hasLink.value) return undefined;
  return canAccess(store.state.auth.user, link.route.value?.meta?.roles as string[] | undefined);
});

const {t} = useI18n();
useTitle(computed(() => {
  let pageTitle = '';

  if (route.meta.title) {
    const metaTitle = route.meta.title as Title;
    const text = typeof metaTitle === 'string' ? metaTitle : metaTitle.text;

    if (typeof text === 'string') {
      pageTitle += `${t(text)} | `;
    }
  }

  if (header?.title && header?.title.length > 0) {
    const lastTitle = unref(header.title.at(-1)) ?? '';
    const text = typeof lastTitle === 'string' ? lastTitle : lastTitle?.text;

    if (typeof text === 'string') {
      pageTitle += `${t(text)} | `;
    }
  }

  return `${pageTitle}Working Bicycle`;
}));
</script>

<style lang="scss" scoped>
.link {
  @apply flex gap-x-4 items-center;
  &:hover {
    h1 {
      @apply text-primary;
    }
  }
}

.back-link {
  @apply block h-3.5 w-auto rotate-90 text-current;

  :deep(svg) {
    @apply h-full w-auto;
  }

  :deep(path) {
    @apply stroke-2;
  }
}
</style>
